import type { ZodType, ZodTypeDef } from "zod";
import { z } from "zod";
import { AssetKind, type AssetRef, type ToolCategory } from "./domain";
import type { PipelineSpec } from "./pipeline";
import type { ProviderRegistry } from "./provider";
import type { CapabilityRegistry } from "./capability";
import type { ToolModelDef } from "./tool-models";

// §3.4 Tool-Manifest (Split ToolCore/ToolUI, D16) ──────────────────────────────

export const InputRequirement = z.object({
  kind: AssetKind,
  /**
   * Media-Union-Slot (D132): akzeptierte Asset-Kinds ZUSÄTZLICH zur Anzeige-/Default-Kind
   * `kind`. Fehlt das Feld, akzeptiert der Slot exakt `kind` (byte-identisches Alt-Verhalten).
   * Der Submit (`resolveInputs`) prüft gegen die Union und reicht den ECHTEN Asset-Kind in
   * die AssetRefs — `buildPipeline` kann damit modalitäts-abhängig branchen (chroma-key-
   * remover: image → key(image), video → key(video)→video-encode).
   */
  kinds: z.array(AssetKind).optional(),
  min: z.number().int().nonnegative(),
  max: z.number().int().nonnegative(),
  role: z.string(),
  /**
   * Rein UI-deklarative Sichtbarkeit (D91): der Slot wird in der Eingabe-Spalte NUR gezeigt, wenn
   * `settings[setting] === equals` (z.B. `end-frame` nur bei `loop===false`). Der Server IGNORIERT
   * das Feld (validiert weiter min/max/role/kind); ein optionaler, ausgeblendeter Slot bleibt also
   * submit-sicher. Hält die Shell generisch (kein Tool-Hardcoding, §1/§4).
   */
  visibleWhen: z.object({ setting: z.string(), equals: z.unknown() }).optional(),
});
export type InputRequirement = z.infer<typeof InputRequirement>;

export const OutputSlot = z.object({
  name: z.string(),
  kind: AssetKind,
  optional: z.boolean(),
  collection: z.boolean().optional(), // collection = Fan-out (D42)
});
export type OutputSlot = z.infer<typeof OutputSlot>;

export interface BuildPipelineContext {
  providers: ProviderRegistry;
  capabilities: CapabilityRegistry;
  priceBookVersion: number;
  now: string;
}

export interface ToolCore<TSettings = unknown> {
  readonly id: string;
  readonly category: ToolCategory;
  /** nur Assets { kind, min, max, role } */
  readonly inputs: InputRequirement[];
  /**
   * statisch + Schema-Factory(provider) für Provider-Bounds (D12/D44). Input-Typ
   * `unknown`: `.parse(unknown) → TSettings`; lässt Schemas mit `.default()`/`.refine()`
   * zu (deren Input-Form ≠ Output-Form TSettings ist).
   */
  readonly settingsSchema: ZodType<TSettings, ZodTypeDef, unknown>;
  /**
   * D101: wählbare Modelle (Dropdown, §11) — deklarative Profile über dem Settings-Schema
   * (Defaults/hiddenSettings/providerBindings). `models[0]` = Default. Das `model`-Feld im
   * `settingsSchema` MUSS via `modelEnum(models)` aus DIESER Liste abgeleitet sein
   * (Single Source); das Gating läuft über `withModelGating` im Schema selbst.
   */
  readonly models?: readonly ToolModelDef<TSettings>[];
  /**
   * §3.4/§3.7/D44 Schema-Factory(provider): provider-gebundenes Settings-Schema für die
   * 2. Submit-Validierung (Provider-Bounds, z.B. maxDurationS/maxFps). Tools OHNE
   * provider-abhängige Bounds lassen das weg; der Submit fällt dann auf `settingsSchema`
   * zurück. Wird gesetzt, MUSS der Submit hiermit validieren (sonst stilles Clamp statt
   * Ablehnung). `settingsSchema` bleibt das statische Basis-Schema (großzügige Bounds).
   * D101: `rawSettings` = die UNGEPARSTEN Request-Settings (two-pass: `selectModel` liest
   * nur `model` → die Bounds spannen sich über den MODELL-gebundenen Provider). Pflicht-
   * Param, damit der Compiler alle Call-Sites erzwingt; modell-lose Tools ignorieren ihn.
   */
  boundSettingsSchema?(ctx: BuildPipelineContext, rawSettings: unknown): ZodType<TSettings, ZodTypeDef, unknown>;
  readonly outputSlots: OutputSlot[];
  buildPipeline(settings: TSettings, inputs: AssetRef[], ctx: BuildPipelineContext): PipelineSpec;
}

export interface IconRef {
  name: string;
}

/**
 * Dependency-freie Abstraktion von React.ComponentType (kein React-Dep im Core).
 * Strukturell kompatibel mit `React.ComponentType<P>`; das Web-Frontend (§11)
 * setzt echte React-Komponenten ein.
 */
export type ComponentType<P = Record<string, unknown>> = (props: P) => unknown;

export interface SettingsUIProps<TSettings> {
  value: TSettings;
  onChange: (next: TSettings) => void;
}
export interface ResultRendererProps {
  assets: AssetRef[];
}

/**
 * ToolUI lebt nur im Web-Frontend (§11) — für die Backbone-Schicht bewusst
 * out-of-scope, aber der Typvertrag aus §3.4 bleibt erhalten (React.ComponentType).
 * Fehlt eine ToolUI, fällt der Result-Viewer auf den kind-basierten Fallback-Renderer (D32/D43).
 */
export interface ToolUI<TSettings = unknown> {
  readonly id: string;
  readonly name: string;
  readonly icon: IconRef;
  readonly SettingsUI: ComponentType<SettingsUIProps<TSettings>>;
  readonly ResultRenderer: ComponentType<ResultRendererProps>;
}

// §3.5 Tool-Registry
export interface ToolRegistry {
  register(core: ToolCore, ui: () => Promise<{ default: ToolUI }>): void;
  getCore(id: string): ToolCore | undefined;
  loadUI(id: string): Promise<ToolUI | undefined>;
  list(filter?: { category?: ToolCategory }): ToolCore[];
}
